import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { auditCsv, auditDetail, auditListPath } from '../../core/parity.js';
import { downloadCsv } from './csv.js';

const PAGE = 20;

/**
 * Admin audit log: paged rows, action filter, CSV of the current filter.
 * @param {{ host: HTMLElement, dead: () => boolean }} ctx
 */
export function mountAudit(ctx) {
  const state = {
    loading: true,
    fatal: '',
    logs: [],
    page: 1,
    pages: 1,
    total: 0,
    action: '',
    actions: [],
  };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(6));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: 'Audit log unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: () => load(true) }),
      }));
      return;
    }
    ctx.host.append(panel());
  }

  function panel() {
    const filter = el('select', {
      class: 'set-input',
      'aria-label': 'Filter by action',
      onChange: (event) => {
        state.action = event.target.value;
        state.page = 1;
        load(false);
      },
    },
    el('option', { value: '', selected: state.action ? null : true }, 'All actions'),
    ...state.actions.map((row) => el('option', {
      value: row.action,
      selected: row.action === state.action ? true : null,
    }, `${row.action} (${row.count})`)));
    return el('div', { class: 'set-stack' },
      el('div', { class: 'set-panel' },
        el('div', { class: 'set-panel-head' },
          el('div', { class: 'set-panel-title' },
            el('span', {}, 'Audit log'),
            el('span', { class: 'set-count' }, String(state.total))),
          el('div', { class: 'set-form-actions' },
            filter,
            el('button', { type: 'button', class: 'set-btn', onClick: exportCurrent }, 'Export CSV'))),
        state.logs.length ? table() : empty({
          title: 'No audit logs',
          body: state.action ? 'Nothing matches this action.' : 'Activity shows up here as changes are made.',
        }),
        pager()));
  }

  function table() {
    const head = el('tr', {},
      el('th', {}, 'Action'),
      el('th', {}, 'Entity'),
      el('th', {}, 'User'),
      el('th', {}, 'Details'),
      el('th', {}, 'Date'));
    const body = el('tbody', {}, state.logs.map(auditRow));
    return el('div', { class: 'set-table-wrap' },
      el('table', { class: 'set-table' }, el('thead', {}, head), body));
  }

  function pager() {
    if (state.pages <= 1) return null;
    return el('div', { class: 'set-panel-foot' },
      el('span', {}, `Page ${state.page} of ${state.pages}`),
      el('button', {
        type: 'button',
        class: 'set-more',
        disabled: state.page <= 1 ? true : null,
        onClick: () => { state.page -= 1; load(false); },
      }, 'Previous'),
      el('button', {
        type: 'button',
        class: 'set-more',
        disabled: state.page >= state.pages ? true : null,
        onClick: () => { state.page += 1; load(false); },
      }, 'Next'));
  }

  async function load(initial) {
    if (initial) {
      state.loading = true;
      state.fatal = '';
      paint();
    }
    const [data, actions] = await Promise.all([
      api(auditListPath({ page: state.page, limit: PAGE, action: state.action })),
      state.actions.length ? Promise.resolve(null) : api('/audit-logs/actions'),
    ]);
    if (ctx.dead()) return;
    state.loading = false;
    if (!data || data.error) {
      state.fatal = (data && data.error) || 'Could not load the audit log';
      state.logs = [];
      paint();
      return;
    }
    state.fatal = '';
    state.logs = data.logs || [];
    const pagination = data.pagination || {};
    state.page = pagination.page || state.page;
    state.pages = pagination.pages || 1;
    state.total = pagination.total || state.logs.length;
    if (actions && !actions.error) state.actions = actions.actions || [];
    paint();
  }

  function exportCurrent() {
    if (!state.logs.length) {
      toast('Nothing to export for this filter', 'error');
      return;
    }
    const name = state.action ? `audit-logs-${state.action}.csv` : 'audit-logs.csv';
    downloadCsv(name, auditCsv(state.logs));
  }

  load(true);
  return { dispose() {} };
}

function auditRow(log) {
  return el('tr', {},
    el('td', {}, log.action || '—'),
    el('td', {}, log.entity_type || '—'),
    el('td', {}, log.user_name || log.user_email || '—'),
    el('td', {}, auditDetail(log) || '—'),
    el('td', {}, when(log.created_at)));
}

function when(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}
